import { useEffect, useState } from 'react'
import { DateTime } from 'luxon'

import { Card, Label, Table, Text, Separator } from '@/ui'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  adminFetchAdvisoryProfile,
  adminFetchUpriseMessages,
  adminFetchUserAdvisoryCalls,
  FinancialAdvisoryProfile,
} from '@/features/financial-advisory/financialAdvisory.slice'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { UserFinancialAdvisoryCallEvent } from '@/features/financial-advisory/userFinancialAdvisoryCallEvents.slice'
import { TypographyStyles } from '@/styles'

export const FinancialAdvisoryPanel = ({ userId }: { userId: number }) => {
  const dispatch = useAppDispatch()
  const [faProfile, setFaProfile] = useState<
    FinancialAdvisoryProfile | undefined
  >()
  const [callEvents, setCallEvents] = useState<
    UserFinancialAdvisoryCallEvent[] | undefined
  >()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [upriseMessages, setUpriseMessages] = useState<any[]>()
  useEffect(() => {
    const fetchData = async () => {
      const faProfile = await adminFetchAdvisoryProfile(userId)(dispatch)
      const events = await adminFetchUserAdvisoryCalls(userId)(dispatch)
      const messages = await adminFetchUpriseMessages(userId)(dispatch)
      setCallEvents(events)
      setFaProfile(faProfile)
      setUpriseMessages(messages)
    }
    fetchData()
  }, [dispatch, userId])

  return (
    <Card className="w-full">
      {faProfile && (
        <>
          <Text variant={TypographyStyles.H2}>
            Financial Advisory: <Label color="green">User Has Advisory</Label>
          </Text>
          <br />
          <Text variant={TypographyStyles.H3}>Advisory Status:</Text>
          <Text>{faProfile?.status}</Text>
          <br />
          <Text variant={TypographyStyles.H3}>Pilot Status:</Text>
          <Text>{faProfile?.pilotStatus}</Text>
          <br />
          <Text variant={TypographyStyles.H3}>Started Enrollment On:</Text>
          <Text>
            {DateTime.fromISO(faProfile?.createdAt ?? '').toFormat(
              DATE_FORMATS_LUXON.DISPLAY_SHORT
            )}
          </Text>
          <br />
          <Text variant={TypographyStyles.H3}>Completed Onboarding On:</Text>
          <Text>
            {faProfile?.onboardingCompletedAt
              ? DateTime.fromISO(faProfile.onboardingCompletedAt).toFormat(
                  DATE_FORMATS_LUXON.DISPLAY_SHORT
                )
              : 'None'}
          </Text>
          <br />
          <Text variant={TypographyStyles.H3}>Uprise Details:</Text>
          <ul>
            <li>
              <b>Uprise UUID:</b> {faProfile?.upriseUserUuid}
            </li>
            <li>
              <b>Uprise Financial Plan UUID:</b>{' '}
              {faProfile?.upriseSubscriptionUuid}
            </li>
            <li>
              <b>Uprise Subscription UUID:</b>{' '}
              {faProfile?.upriseFinancialPlanUuid}{' '}
            </li>
            <li>
              <b>Created At: </b>
              {DateTime.fromISO(faProfile.createdAt).toFormat(
                DATE_FORMATS_LUXON.DISPLAY_SHORT
              )}
            </li>
          </ul>
          <Separator />
          <Text variant={TypographyStyles.H2}>Advisory Calls</Text>
          <Table>
            <Table.Header>
              <Table.HeaderCell>Id</Table.HeaderCell>
              <Table.HeaderCell>Event Date Time</Table.HeaderCell>
              <Table.HeaderCell>Event Name</Table.HeaderCell>
              <Table.HeaderCell>Scheduled At</Table.HeaderCell>
            </Table.Header>
            <Table.Body>
              {callEvents?.map((event) => (
                <Table.Row key={event.id}>
                  <Table.Cell>{event?.id}</Table.Cell>
                  <Table.Cell>
                    Start time:{' '}
                    {DateTime.fromISO(event.startTime).toFormat(
                      DATE_FORMATS_LUXON.DISPLAY_FULL
                    )}
                    <br />
                    End time:{' '}
                    {DateTime.fromISO(event.endTime).toFormat(
                      DATE_FORMATS_LUXON.DISPLAY_FULL
                    )}
                  </Table.Cell>
                  <Table.Cell>{event.rawCalendlyEventBlob?.name}</Table.Cell>
                  <Table.Cell>
                    {DateTime.fromISO(event.createdAt).toFormat(
                      DATE_FORMATS_LUXON.DISPLAY_FULL
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <Separator />
          <Text variant={TypographyStyles.H2}>Uprise Messages</Text>
          <Text>Table of all messages between Uprise and User</Text>
          <Table>
            <Table.Header>
              <Table.HeaderCell>Message Id</Table.HeaderCell>
              <Table.HeaderCell>Message Sent At</Table.HeaderCell>
              <Table.HeaderCell>Message Subject</Table.HeaderCell>
              <Table.HeaderCell>Message Content</Table.HeaderCell>
              <Table.HeaderCell>Sender</Table.HeaderCell>
              <Table.HeaderCell>Recipient</Table.HeaderCell>
            </Table.Header>
            <Table.Body>
              {upriseMessages &&
                Object.keys(upriseMessages).map((key: string) => {
                  const message =
                    upriseMessages[key as keyof typeof upriseMessages]?.message
                  return (
                    <Table.Row key={key}>
                      <Table.Cell>{key}</Table.Cell>
                      <Table.Cell>
                        {DateTime.fromISO(message.updatedAt).toFormat(
                          DATE_FORMATS_LUXON.DISPLAY_FULL
                        )}
                      </Table.Cell>
                      <Table.Cell>{message?.subject}</Table.Cell>
                      <Table.Cell>{message?.content}</Table.Cell>
                      <Table.Cell>
                        {message?.senderFirstName} {message?.senderLastName}
                      </Table.Cell>
                      <Table.Cell>
                        {message?.recipientFirstName}{' '}
                        {message?.recipientLastName}
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
            </Table.Body>
          </Table>
        </>
      )}
      {!faProfile && (
        <>
          <Text variant={TypographyStyles.H2}>
            Financial Advisory: <Label color="red">No Advisory</Label>
          </Text>
          <Text variant={TypographyStyles.H3}>
            User has not enrolled Financial Advisory
          </Text>
        </>
      )}
    </Card>
  )
}
